import React, { useState } from 'react';
import './styles.css';
import './config.css';
import NavRail from './NavRail';
import { TweaksPanel, TweakSection, TweakRadio, useTweaks } from './TweaksPanel';
import { mergeDefaults } from './themePersist';
import {
  TREE, ROW_DATA, CELL_PREVIEWS, COMPANIES_LITE,
  TreeNode, TreeRow, TreeSection, RowDataItem, RowSource, CellPreview,
} from './configData';

const TWEAK_DEFAULTS = {
  theme: 'paper' as 'paper' | 'light' | 'dark',
  density: 'regular' as 'compact' | 'regular' | 'comfy',
  variant: 'safe' as 'safe' | 'bold',
};

const fmt = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(Math.round(n));
  return sign + abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function TitleBar() {
  return (
    <div className="titlebar">
      <span className="brand">XELENCE</span>
      <span className="sep">/</span>
      <span className="crumb">Configs</span>
      <span className="sep">/</span>
      <span className="crumb"><b>full_sgd_v3</b></span>
      <div className="right">
        <div className="stat"><span className="k">version</span><span className="v">v.3.2</span></div>
        <div className="stat"><span className="k">draft</span><span className="v" style={{ color: 'var(--override)' }}>● 2 несохр.</span></div>
        <div className="stat"><span className="k">last save</span><span className="v">28 апр 14:22</span></div>
      </div>
    </div>
  );
}

function Toolbar() {
  return (
    <div className="config-toolbar">
      <div className="picker">
        <div>
          <div className="lbl">CONFIG</div>
          <div className="val">full_sgd_v3<span className="meta">Полный СГД + расшифровки</span></div>
        </div>
        <span style={{ color: 'var(--ink-4)' }}>▾</span>
      </div>
      <div className="meta-strip">
        <span><span style={{ color: 'var(--ink-4)' }}>sections</span> <b>3</b></span>
        <span><span style={{ color: 'var(--ink-4)' }}>rows</span> <b>41</b></span>
        <span><span style={{ color: 'var(--ink-4)' }}>files</span> <b>5</b></span>
        <span><span style={{ color: 'var(--ink-4)' }}>preview cluster</span> <b>dd_1q26</b></span>
      </div>
      <div className="actions">
        <button>↶ undo</button>
        <button>↷ redo</button>
        <button>⊕ test against cluster</button>
        <button className="primary">⤓ save draft</button>
        <button className="primary">⏎ publish v.3.3</button>
      </div>
    </div>
  );
}

function TreePane({ tree, selectedId, setSelectedId }: {
  tree: TreeNode[]; selectedId: string; setSelectedId: (id: string) => void;
}) {
  const sections = tree.filter((n): n is TreeSection => n.kind === 'section');
  return (
    <div className="tree-pane">
      <div className="ph">
        <span className="t">Структура отчёта</span>
        <button className="add">+ строка</button>
      </div>
      <div className="search-row">
        <input placeholder="найти строку…" />
      </div>
      {sections.map((s) => {
        const children = tree.filter(
          (n): n is TreeRow => n.kind === 'row' && n.parent === s.id
        );
        return (
          <div key={s.id}>
            <div className="tree-section">
              <span className="num">{s.num}</span>
              <span className="name">{s.name}</span>
              <span className="ct">{children.length} стр.</span>
            </div>
            {children.map((r) => (
              <div
                key={r.id}
                className={`tree-node ${selectedId === r.id ? 'selected' : ''} ${r.isTotal ? 'total' : ''}`}
                onClick={() => setSelectedId(r.id)}
                style={{ paddingLeft: 14 + r.level * 14 }}
              >
                <span className="ind">{r.level === 1 ? '└' : r.isTotal ? 'Σ' : '·'}</span>
                <span className="name">{r.name}</span>
                {r.hasIssue && <span className="iss" title="Есть валидационная ошибка" />}
                <span className="meta">{r.sources ? `${r.sources} src` : ''}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

type HoverState = { anchor: HTMLElement; source: RowSource } | null;

function CellChip({ s, onHover }: { s: RowSource; onHover: (a: HTMLElement | null, s?: RowSource) => void }) {
  const isRef = s.file === '↩ ref';
  return (
    <span
      className={`tok ${isRef ? 'ref' : ''}`}
      onMouseEnter={(e) => !isRef && onHover(e.currentTarget, s)}
      onMouseLeave={() => onHover(null)}
    >
      <span className="file">{s.file}</span>
      <span style={{ color: 'var(--ink-4)' }}>!</span>
      <span className="cell">{s.cell}</span>
      <span className="preview">{fmt(s.preview)}</span>
    </span>
  );
}

function FormulaBuilder({ row, onHoverChip }: { row: RowDataItem; onHoverChip: (a: HTMLElement | null, s?: RowSource) => void }) {
  const isTotal = row.isTotal;
  return (
    <div className="formula-builder">
      <div className="formula-line">
        {row.sources.map((s, i) => (
          <React.Fragment key={i}>
            <CellChip s={s} onHover={onHoverChip} />
            {i < row.sources.length - 1 && (
              <span className="op">{isTotal && row.id === 'r6' ? '−' : '+'}</span>
            )}
          </React.Fragment>
        ))}
        {!isTotal && (
          <>
            <span className="op">+</span>
            <button className="tok add-tok">+ добавить ссылку</button>
          </>
        )}
      </div>
      <div className="formula-foot">
        <button className="btn">f(x) функция</button>
        <button className="btn">⓪ константа</button>
        <button className="btn">⊕ ссылка на строку</button>
        <span className="res">результат · <b>{fmt(row.preview.dd)}</b> для dd_1q26</span>
      </div>
    </div>
  );
}

function SourcesTable({ sources, onHoverChip }: { sources: RowSource[]; onHoverChip: (a: HTMLElement | null, s?: RowSource) => void }) {
  return (
    <div className="src-table">
      <div className="h">
        <div></div>
        <div>Файл</div>
        <div>Ячейка</div>
        <div>Описание</div>
        <div className="n">Значение</div>
        <div></div>
      </div>
      {sources.map((s, i) => (
        <div
          key={i}
          className="r"
          onMouseEnter={(e) => s.file !== '↩ ref' && onHoverChip(e.currentTarget, s)}
          onMouseLeave={() => onHoverChip(null)}
        >
          <div style={{ color: 'var(--ink-4)', fontFamily: 'var(--mono)' }}>{i + 1}</div>
          <div className="file">{s.file}</div>
          <div className="cell">{s.cell}</div>
          <div className="label">{s.label}</div>
          <div className="v n">{fmt(s.preview)}</div>
          <div className="x">×</div>
        </div>
      ))}
      <div className="add-row">+ добавить источник</div>
    </div>
  );
}

function CellPopover({ hover }: { hover: HoverState }) {
  if (!hover) return null;
  const { anchor, source } = hover;
  const key = `${source.file}_${source.cell}`;
  const preview: CellPreview | undefined = CELL_PREVIEWS[key];
  if (!preview) return null;
  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + 6;
  const left = Math.min(rect.left, window.innerWidth - 380);
  return (
    <div className="cell-pop" style={{ top, left }}>
      <div className="cph">
        <span className="ftag">{preview.file}</span>
        <span className="sheet">· {preview.sheet}</span>
        <span className="cell">{preview.cell}</span>
        <span className="lbl">{preview.label}</span>
      </div>
      <table className="cgrid">
        <tbody>
          {preview.grid.map((row, ri) => (
            <tr key={ri} className={ri === 0 ? 'h' : ''}>
              {row.map((cell, ci) => {
                const isHl = ri === preview.hl.r && ci === preview.hl.c;
                const isRowHdr = ci === 0;
                return (
                  <td
                    key={ci}
                    className={`${isRowHdr ? 'rh' : ''} ${isHl ? 'hl' : ''} ${ci > 1 && ri > 0 ? 'n' : ''}`}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cpf">↗ открыть файл в исходнике</div>
    </div>
  );
}

function MasterMap({ row }: { row: RowDataItem }) {
  const m = row.masterMapping;
  return (
    <div className="master-map">
      <div className="field">
        <div className="lbl">Лист</div>
        <div className="v">{m.sheet}</div>
      </div>
      <div className="field">
        <div className="lbl">Ячейка</div>
        <div className="v">{m.cell}</div>
      </div>
      <div className="field">
        <div className="lbl">Подпись в master</div>
        <div className="v" style={{ fontFamily: 'var(--sans)' }}>{m.label}</div>
      </div>
    </div>
  );
}

function Validation({ rules }: { rules: RowDataItem['validationRules'] }) {
  return (
    <div className="valid-list">
      {rules.map((r) => (
        <div key={r.id} className={`valid-rule ${!r.ok ? 'bad' : ''}`}>
          <span className="dot" />
          <span className="lbl">{r.label}</span>
          {r.msg && <span className="msg">{r.msg}</span>}
        </div>
      ))}
      <div className="valid-rule" style={{ background: 'var(--bg-elev)', color: 'var(--ink-3)', cursor: 'pointer' }}>
        <span style={{ color: 'var(--accent)' }}>+</span>
        <span className="lbl" style={{ color: 'var(--accent)' }}>добавить правило</span>
      </div>
    </div>
  );
}

function EditorPane({ row, sectionName, onHoverChip }: {
  row: RowDataItem; sectionName: string;
  onHoverChip: (a: HTMLElement | null, s?: RowSource) => void;
}) {
  return (
    <div className="editor-pane">
      <div className="breadcrumb">
        <span>full_sgd_v3</span>
        <span className="sep">/</span>
        <span>{sectionName}</span>
        <span className="sep">/</span>
        <span style={{ color: 'var(--ink)' }}>{row.name}</span>
      </div>
      <h1>{row.name}</h1>

      <div className="row-meta">
        <div className="field">
          <div className="lbl">id</div>
          <div className="v">{row.id}</div>
        </div>
        <div className="field">
          <div className="lbl">level</div>
          <div className="v">{row.level}</div>
        </div>
        <div className="field">
          <div className="lbl">тип</div>
          <div className="v sans">{row.isTotal ? 'итоговая строка' : 'обычная строка'}</div>
        </div>
        <div className="field">
          <div className="lbl">единица</div>
          <div className="v">KZT</div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <div className="lbl">описание</div>
          <div className="v sans" style={{ color: 'var(--ink-2)' }}>{row.description}</div>
        </div>
      </div>

      <h3>Формула</h3>
      <FormulaBuilder row={row} onHoverChip={onHoverChip} />

      <h3>Источники</h3>
      <SourcesTable sources={row.sources} onHoverChip={onHoverChip} />

      <h3>Привязка к master.xlsx</h3>
      <MasterMap row={row} />

      <h3>Валидация</h3>
      <Validation rules={row.validationRules} />
    </div>
  );
}

function PreviewPane({ row }: { row: RowDataItem }) {
  const values = row.preview;
  return (
    <div className="preview-pane">
      <div className="ph">
        <span className="t">Live preview</span>
        <span className="live"><span className="dot" />пересчёт ~80мс</span>
      </div>

      <div className="pr-block">
        <div className="h">Значение по кластерам</div>
        {COMPANIES_LITE.map((c) => {
          const v = values[c.id] ?? 0;
          return (
            <div key={c.id} className={`pr-cluster ${c.id === 'dd' ? 'curr' : ''}`}>
              <span className="code">{c.short}</span>
              <span className="name">{c.name}</span>
              <span className="v">{fmt(v)}</span>
            </div>
          );
        })}
      </div>

      <div className="pr-block">
        <div className="h">Используется в формулах</div>
        <div className="impact">
          {row.usedIn.length === 0 ? (
            <span style={{ color: 'var(--ink-4)' }}>не используется в других расчётах</span>
          ) : (
            <>
              Эта строка используется в <b>{row.usedIn.length} {row.usedIn.length === 1 ? 'расчёте' : 'расчётах'}</b>:<br />
              {row.usedIn.map((u) => (
                <div key={u.id}>· <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{u.id}</span> {u.name}</div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="pr-block">
        <div className="h">История изменений</div>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', lineHeight: 1.7, fontFeatureSettings: "'tnum'" }}>
          <div>28.04 14:22 · Г. Касенова · добавлен F441</div>
          <div>15.04 11:00 · Г. Касенова · создана</div>
        </div>
      </div>

      <div className="pr-block" style={{ borderBottom: 0 }}>
        <div className="h">Затронутые отчёты</div>
        <div className="impact">
          <b>4 кластера</b> используют эту строку при пересчёте.<br />
          Изменения вступят в силу после <b>publish v.3.3</b>.
        </div>
      </div>
    </div>
  );
}

export default function Configs() {
  const [t, setTweak] = useTweaks(mergeDefaults(TWEAK_DEFAULTS));
  const [selectedId, setSelectedId] = useState('r3');
  const [hover, setHover] = useState<HoverState>(null);

  const row = ROW_DATA[selectedId];
  const node = TREE.find((n) => n.id === selectedId);
  const section = node && node.kind === 'row'
    ? TREE.find((s): s is TreeSection => s.kind === 'section' && s.id === node.parent)
    : undefined;

  const onHoverChip = (anchor: HTMLElement | null, source?: RowSource) => {
    if (!anchor || !source) setHover(null);
    else setHover({ anchor, source });
  };

  return (
    <div className="shell" data-theme={t.theme} data-density={t.density} data-variant={t.variant}>
      <NavRail />
      <div className="config-page">
        <TitleBar />
        <Toolbar />
        <div className="config-body">
          <TreePane tree={TREE} selectedId={selectedId} setSelectedId={setSelectedId} />
          {row ? (
            <EditorPane row={row} sectionName={section?.name || ''} onHoverChip={onHoverChip} />
          ) : (
            <div className="editor-pane">
              <div style={{ color: 'var(--ink-4)', fontSize: 12, marginTop: 80, textAlign: 'center' }}>
                выберите строку для редактирования
              </div>
            </div>
          )}
          {row ? <PreviewPane row={row} /> : <div className="preview-pane" />}
        </div>
        <CellPopover hover={hover} />
      </div>

      <TweaksPanel>
        <TweakSection label="Тема" />
        <TweakRadio label="Theme" value={t.theme}
          options={['paper', 'light', 'dark']}
          onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Вариант" />
        <TweakRadio label="Variant" value={t.variant}
          options={['safe', 'bold']}
          onChange={(v) => setTweak('variant', v)} />
        <TweakSection label="Плотность" />
        <TweakRadio label="Density" value={t.density}
          options={['compact', 'regular', 'comfy']}
          onChange={(v) => setTweak('density', v)} />
      </TweaksPanel>
    </div>
  );
}
